import type { ProviderDetails, SeriesItem, VodItem } from "@hybrid-iptv/types";
import type { TitleCastMember } from "./TitleDetail.js";
import { firstTrailerUrl, type TmdbDetailsShape } from "./vodHooks.js";

/**
 * What a title page shows, from the provider's own details first and TMDB second
 * (Section 26 step 1). The provider's panel copied its text from TMDB, so the two
 * agree; taking the panel's first means the page is complete without TMDB, and
 * TMDB only fills what the panel left out.
 *
 * Pictures stay TMDB-first while TMDB is on: a dead provider picture has no
 * fallback on the page, and the panel's pictures are TMDB's anyway. Cast keeps
 * TMDB's photos when it has a cast; otherwise the panel's names, shown with
 * initials.
 */
export interface TitleFields {
  /** "2023 · 2h 53m". */
  subtitle?: string;
  overview?: string;
  posterUrl?: string;
  backdropUrl?: string;
  rating?: number;
  genres?: string[];
  cast?: TitleCastMember[];
  director?: string;
  trailerUrl?: string;
  runtimeMinutes?: number;
  year?: number;
}

/** TMDB's picture summary, where a match gave one before the details arrived. */
export interface TmdbSummaryFields {
  overview?: string;
  vote_average?: number;
}

const tmdbImage = (path: string | null | undefined, size: "w342" | "w780") =>
  path ? `https://image.tmdb.org/t/p/${size}${path}` : undefined;

/** 173 → "2h 53m"; 45 → "45m". */
export function runtimeLabel(minutes: number | undefined): string | undefined {
  if (!minutes || minutes <= 0) return undefined;
  const hours = Math.floor(minutes / 60);
  const rest = Math.round(minutes % 60);
  if (hours === 0) return `${rest}m`;
  return rest === 0 ? `${hours}h` : `${hours}h ${rest}m`;
}

/** The panel's cast names as cards (no photo: initials). Negative ids never meet TMDB's. */
export function providerCast(names: string[] | undefined): TitleCastMember[] | undefined {
  if (!names || names.length === 0) return undefined;
  return names.map((name, index) => ({ id: -(index + 1), name }));
}

function shared(
  provider: ProviderDetails | undefined,
  tmdb: TmdbDetailsShape | undefined,
  item: { posterUrl?: string; backdropUrl?: string; rating?: number; trailerUrl?: string } | undefined,
  itemYear: number | undefined,
  summary: TmdbSummaryFields | undefined,
  runtimeFromTmdb: boolean,
): TitleFields {
  const yearText = provider?.releaseDate?.slice(0, 4) ?? tmdb?.release_date?.slice(0, 4) ?? (itemYear ? String(itemYear) : undefined);
  const year = Number(yearText) || undefined;
  const tmdbRuntime = runtimeFromTmdb && typeof tmdb?.runtime === "number" && tmdb.runtime > 0 ? tmdb.runtime : undefined;
  const runtimeMinutes = provider?.runtimeMinutes ?? tmdbRuntime;
  const subtitle = [yearText, runtimeFromTmdb ? runtimeLabel(runtimeMinutes) : undefined].filter(Boolean).join(" · ") || undefined;
  const tmdbCast = tmdb?.credits?.cast ?? [];
  return {
    subtitle,
    overview: provider?.overview ?? tmdb?.overview ?? summary?.overview,
    posterUrl: tmdbImage(tmdb?.poster_path, "w342") ?? item?.posterUrl,
    backdropUrl: tmdbImage(tmdb?.backdrop_path, "w780") ?? provider?.backdropUrl ?? item?.backdropUrl,
    rating: provider?.rating ?? tmdb?.vote_average ?? summary?.vote_average ?? item?.rating,
    genres: provider?.genres ?? tmdb?.genres?.map((genre) => genre.name),
    cast: tmdbCast.length > 0 ? tmdbCast : providerCast(provider?.cast),
    director: provider?.director,
    trailerUrl: firstTrailerUrl(tmdb, item?.trailerUrl ?? provider?.trailerUrl),
    runtimeMinutes,
    year,
  };
}

/** A film's page: the panel's details (get_vod_info), then TMDB's. */
export function movieTitleFields(
  movie: VodItem | null | undefined,
  provider: ProviderDetails | undefined,
  tmdb: TmdbDetailsShape | undefined,
  summary?: TmdbSummaryFields,
): TitleFields {
  return shared(provider, tmdb, movie ?? undefined, movie?.year, summary, true);
}

/**
 * A series' page: the panel's details (get_series_info), then TMDB's. A series'
 * runtime is per episode and is not shown in the header.
 */
export function seriesTitleFields(
  series: SeriesItem | null | undefined,
  provider: ProviderDetails | undefined,
  tmdb: TmdbDetailsShape | undefined,
): TitleFields {
  return shared(provider, tmdb, series ?? undefined, undefined, undefined, false);
}
